"use client";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { H6, Paragraph } from "src/lib/components/salify-next-layout/default/components/Typography";
import { FlexBox } from "src/lib/components/salify-next-layout/default/components/flex-box";

import SalifyLink from "src/lib/components/link/salify-link";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import PinterestIcon from "@mui/icons-material/Pinterest";
import InstagramIcon from "@mui/icons-material/Instagram";
import React from "react";
import styles from "src/lib/components/footer/footer.default.module.css";
import SalifyFallback from "src/lib/components/salify-fallback/default";
import { layoutConstant } from "src/lib/utils/constants";
import Image from "next/image";
import { toAssetImages } from "src/lib/utils";
import { styled } from "@mui/material/styles";

export interface VariantProps {
  companyInfo?: Array<{ label: string; value: string }>;
  businessCompanyInfo?: Array<{ label: string; value: string }>;
  aboutUs: Array<{ label: string; url: string }>;
  customerCare: Array<{ label: string; url: string }>;
  contactUs: {
    address: string;
    email: string;
    phone: string;
  };
  businessContactUs: {
    address: string;
    email: string;
    phone: string;
  };
  socialItems: Array<{
    type: "facebook" | "twitter" | "youtube" | "pinterest" | "instagram";
    url: string;
  }>;
}

const ICON_MAPPING: {
  [key in "facebook" | "twitter" | "youtube" | "pinterest" | "instagram"]: any;
} = {
  facebook: FacebookIcon,
  twitter: TwitterIcon,
  youtube: YouTubeIcon,
  pinterest: PinterestIcon,
  instagram: InstagramIcon,
};

const StyledImgage = styled(Image)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    marginLeft: "auto",
    marginRight: "auto",
  },
}));

const FooterBazaar: React.FC<VariantProps> = (props) => {
  const SocialLinks = () => {
    if (!props.socialItems.length) {
      return null;
    }
    return (
      <FlexBox className="flex" mx={-0.625}>
        {props.socialItems.map(({ type, url }, ind) => {
          const Icon = (ICON_MAPPING as any)[type];
          return Icon ? (
            <SalifyLink href={url} key={ind}>
              <Icon
                sx={{
                  fontSize: 16,
                }}
              />
            </SalifyLink>
          ) : null;
        })}
      </FlexBox>
    );
  };

  return (
    <Box
      component="footer"
      bgcolor="#022213"
      mb={{
        md: 0,
        xs: `${layoutConstant.mobileNavHeight}px`,
      }}
    >
      <Box
        bgcolor="#222935"
        sx={{
          backgroundImage: `url('${toAssetImages("misc/footer-bg.png")}')`,
        }}
      >
        <Box
          component={Container}
          pt={{
            sm: 8,
            xs: 4,
          }}
          pb={{
            sm: 2,
          }}
          color="white"
          overflow="hidden"
        >
          <Grid container spacing={3}>
            <Grid item lg={2} md={6} sm={6} xs={12}>
              <SalifyFallback.Label path="title" />

              <Paragraph mb={2.5} mt={1.5} color="grey.500">
                <SalifyFallback.Label path="description" />
              </Paragraph>
            </Grid>

            <Grid item lg={2} md={6} sm={6} xs={12}>
              <H6 fontSize={18} mb={1.5} lineHeight="1" color="white">
                <SalifyFallback.Label path="aboutUs" />
              </H6>

              {props.aboutUs.map((item, ind) => (
                <Paragraph key={ind}>
                  <SalifyLink href={item.url} className={styles.title}>
                    {item.label}
                  </SalifyLink>
                </Paragraph>
              ))}
            </Grid>

            <Grid item lg={2} md={4} sm={4} xs={12}>
              <H6 fontSize={18} mb={1.5} lineHeight="1" color="white">
                <SalifyFallback.Label path="customerCare" />
              </H6>

              {props.customerCare.map((item, ind) => (
                <Paragraph key={ind}>
                  <SalifyLink href={item.url} className={styles.title}>
                    {item.label}
                  </SalifyLink>
                </Paragraph>
              ))}
            </Grid>

            <Grid item lg={3} md={8} sm={8} xs={12}>
              <H6 fontSize={18} mb={1.5} lineHeight="1" color="white">
                <SalifyFallback.Label path="contactUs" />
              </H6>

              {props.companyInfo &&
                props.companyInfo.map((item, ind) => (
                  <Paragraph key={ind} py={0.6} color="grey.500">
                    <b>{item.label}:</b>&nbsp;{item.value}
                  </Paragraph>
                ))}

              <Paragraph py={0.6} color="grey.500">
                {props.contactUs.address}
              </Paragraph>

              <Paragraph py={0.6} color="grey.500">
                <b>Email:</b>&nbsp;{props.contactUs.email}
              </Paragraph>

              <Paragraph py={0.6} mb={2} color="grey.500">
                <b>Phone:</b>&nbsp;{props.contactUs.phone}
              </Paragraph>
            </Grid>

            <Grid item lg={3} md={8} sm={8} xs={12}>
              <H6 fontSize={18} mb={1.5} lineHeight="1" color="white">
                <SalifyFallback.Label path="businessContactUs" default="Kinh doanh" />
              </H6>

              {props.businessCompanyInfo &&
                props.businessCompanyInfo.map((item, ind) => (
                  <Paragraph key={ind} py={0.6} color="grey.500">
                    <b>{item.label}:</b>&nbsp;{item.value}
                  </Paragraph>
                ))}

              <Paragraph py={0.6} color="grey.500">
                {props.businessContactUs.address}
              </Paragraph>

              <Paragraph py={0.6} color="grey.500">
                <b>Email:</b>&nbsp;{props.businessContactUs.email}
              </Paragraph>

              <Paragraph py={0.6} mb={2} color="grey.500">
                <b>Phone:</b>&nbsp;{props.businessContactUs.phone}
              </Paragraph>

              <SocialLinks />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box component={Container} color="grey.500" pb={{ sm: 4 }}>
        <Grid container spacing={3} alignItems="center" py={1}>
          <Grid item sm={8} xs={12} textAlign={{ xs: "center", sm: "left" }}>
            <SalifyFallback.Label path="copyright" />
          </Grid>
          <Grid item sm={4} xs={12}>
            <StyledImgage
              alt=""
              width={160}
              height={60}
              src={toAssetImages("misc/notified-ministry.png")}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FooterBazaar;
